import React, { useRef, useEffect, useState } from 'react';
import s from './DetailModal.module.scss';
import TextSummaryContent from './contents/TextSummaryContent';
import VideoPlayerContent from './contents/VideoPlayerContent';
import VideoPlayer from '@/components/VideoPlayer';
import MindMapContent from './contents/MindMapContent';
import VideoListContent from './contents/VideoListContent';
import VideoDetailContent from './contents/VideoDetailContent';
import ToastUtils from '@/utils/ToastUtils';
import checkmarkImg from '@/assets/images/Multimodal.png';
import copyIconImg from '@/assets/images/web/normal.png';
import ReactDOM from 'react-dom/client';
import MindMap from '@/components/MindMap';
import { CloseOutlined } from '@ant-design/icons';
import { Tooltip, Button } from 'antd';

/**
 * 多模态学习详情侧边抽屉（通用组件）
 * 支持五种类型：
 * - text_summary: 文字总结
 * - video_player: 视频播放器（播客）
 * - mind_map: 思维导图
 * - video_list: 视频列表
 * - video_detail: 视频详情
 */
const DetailModal = ({ visible, data, onClose, videoList = [], onVideoClick, onDetailClick, onContinueAsk, onViewNext, onBackToList }) => {
    const contentRef = useRef(null);
    const drawerRef = useRef(null);

    // 复制 Markdown 内容到剪贴板
    const [copyState, setCopyState] = useState({ status: 'idle' }); // status: idle | success
    const copyTimerRef = useRef(null);

    // 记录抽屉打开时的位置
    const [fixedPosition, setFixedPosition] = useState(null);

    const handleCopyMarkdown = () => {
        if (!data.description) {
            console.warn('没有可复制的内容');
            return;
        }

        // 6 秒冷却期间的点击仅提示，不重复复制
        if (copyState.status === 'success') {
            return;
        }

        navigator.clipboard.writeText(data.description)
            .then(() => {
                setCopyState({ status: 'success' });
                if (copyTimerRef.current) {
                    clearTimeout(copyTimerRef.current);
                }
                copyTimerRef.current = setTimeout(() => setCopyState({ status: 'idle' }), 6000);
            })
            .catch(err => {
                console.error('复制失败:', err);
                ToastUtils.showAbnormalToast('复制失败');
            });
    };

    useEffect(() => {
        return () => {
            if (copyTimerRef.current) {
                clearTimeout(copyTimerRef.current);
            }
        };
    }, []);

    // 当内容数据变化时，滚动到顶部
    useEffect(() => {
        if (contentRef.current && data) {
            contentRef.current.scrollTo({
                top: 0,
                behavior: 'smooth'
            });
        }
    }, [data]);

    // 当抽屉打开时，记录当前位置
    useEffect(() => {
        if (visible && drawerRef.current && !fixedPosition) {
            const rect = drawerRef.current.getBoundingClientRect();
            setFixedPosition({
                left: rect.left,
                right: window.innerWidth - rect.right
            });
        } else if (!visible) {
            // 关闭时重置位置
            setFixedPosition(null);
        }
    }, [visible, fixedPosition]);

    if (!visible || !data) return null;

    // 确定内容类型
    const contentType = data.type || 'text_summary';

    // 判断是否需要显示复制按钮（仅文字总结模式）
    const showCopyButton = contentType === 'text_summary';

    // 判断是否需要显示下载按钮（仅思维导图模式）
    const showDownloadButton = contentType === 'mind_map';

    // 判断是否需要显示返回按钮（仅视频详情模式）
    const showBackButton = contentType === 'video_detail';

    // 根据类型获取标题
    const getTitle = () => {
        if (contentType === 'video_detail') {
            return '视频详情';
        }
        if (contentType === 'video_player') {
            return '播客';
        }
        return data.title || '详情';
    };

    // 下载思维导图
    const handleDownloadMindMap = async () => {
        try {
            let imageDataUrl = '';

            // 等待一小段时间，确保 canvas 完全渲染
            await new Promise(resolve => setTimeout(resolve, 100));

            // 优先直接从 canvas 获取（MindMap 组件渲染的）
            // 这样不会包含任何叠加的 DOM 水印元素
            const mindMapCanvas = contentRef.current?.querySelector('canvas');
            if (mindMapCanvas) {
                // 🔥 创建虚拟的完整尺寸思维导图来获取完整内容
                // 获取思维导图数据（从 MindMapContent 组件中获取）
                const formatMindmapText = (text = '') => {
                    if (!text) return '';
                    const trimmed = text.trim();
                    if (trimmed.startsWith('```')) {
                        return trimmed.replace(/^```mindmap\s*/i, '').replace(/^```/, '').replace(/```$/, '').trim();
                    }
                    return trimmed;
                };

                const parseMindmap = (text = '') => {
                    if (!text) return [];
                    const lines = text
                        .split('\n')
                        .map((line) => line.trim())
                        .filter((line) => !!line);
                    if (!lines.length) return [];

                    const nodes = [];
                    const lastNodeAtDepth = {};

                    lines.forEach((line, index) => {
                        let depth = 1;
                        let label = line;

                        if (/^###\s+/.test(line)) {
                            depth = 2;
                            label = line.replace(/^###\s+/, '');
                        } else if (/^##\s+/.test(line)) {
                            depth = 1;
                            label = line.replace(/^##\s+/, '');
                        } else if (/^-\s+/.test(line)) {
                            depth = 3;
                            label = line.replace(/^-\s+/, '');
                        } else {
                            return;
                        }

                        const node = { id: index, depth, label };

                        if (depth === 1) {
                            node.parentId = undefined;
                        } else {
                            let parent = lastNodeAtDepth[depth - 1];
                            let searchDepth = depth - 1;
                            while (!parent && searchDepth > 0) {
                                searchDepth -= 1;
                                parent = lastNodeAtDepth[searchDepth];
                            }
                            if (parent) {
                                node.parentId = parent.id;
                            } else {
                                const firstLevelNode = nodes.find((n) => n.depth === 1);
                                node.parentId = firstLevelNode ? firstLevelNode.id : undefined;
                            }
                        }

                        nodes.push(node);
                        lastNodeAtDepth[depth] = node;
                    });

                    return nodes;
                };

                const mindmapText = formatMindmapText(data.content || data.description);
                if (!mindmapText) {
                    // 如果没有文本数据，使用当前 canvas（可能是图片）
                    const downloadCanvas = document.createElement('canvas');
                    const ctx = downloadCanvas.getContext('2d');
                    const dpr = window.devicePixelRatio || 1;
                    const srcW = mindMapCanvas.width;
                    const srcH = mindMapCanvas.height;
                    downloadCanvas.width = srcW * dpr;
                    downloadCanvas.height = srcH * dpr;
                    ctx.fillStyle = '#FFFFFF';
                    ctx.fillRect(0, 0, downloadCanvas.width, downloadCanvas.height);
                    ctx.drawImage(mindMapCanvas, 0, 0, srcW, srcH, 0, 0, srcW * dpr, srcH * dpr);
                    imageDataUrl = downloadCanvas.toDataURL('image/png', 1.0);
                } else {
                    // 解析思维导图数据
                    const nodes = parseMindmap(mindmapText);
                    if (!nodes.length) {
                        ToastUtils.showToast('无法解析思维导图数据');
                        return;
                    }

                    const nodeMap = new Map();
                    nodes.forEach((n) => {
                        nodeMap.set(n.id, { title: n.label, children: [] });
                    });

                    const roots = [];
                    nodes.forEach((n) => {
                        const current = nodeMap.get(n.id);
                        if (n.parentId != null && nodeMap.has(n.parentId)) {
                            const parent = nodeMap.get(n.parentId);
                            parent.children.push(current);
                        } else {
                            roots.push(current);
                        }
                    });

                    const rootTitle = data.title && data.title !== '思维导图' ? data.title : roots[0]?.title;
                    const mindmapTree = roots.length === 1 && roots[0]?.title === rootTitle
                        ? { title: rootTitle, children: roots[0]?.children || [] }
                        : { title: rootTitle, children: roots.filter((item) => item.title !== rootTitle).length ? roots.filter((item) => item.title !== rootTitle) : roots };

                    // 🔥 创建隐藏的思维导图容器（使用更大的尺寸，确保内容不重叠）
                    const hiddenContainer = document.createElement('div');
                    hiddenContainer.style.position = 'fixed';
                    hiddenContainer.style.left = '-9999px';
                    hiddenContainer.style.top = '-9999px';
                    hiddenContainer.style.width = '2400px'; // 增加宽度，避免内容重叠
                    hiddenContainer.style.height = '1800px'; // 增加高度，避免内容重叠
                    hiddenContainer.style.visibility = 'hidden';
                    hiddenContainer.style.overflow = 'hidden'; // 防止内容溢出
                    document.body.appendChild(hiddenContainer);

                    // 🔥 创建虚拟的完整尺寸 MindMap 组件
                    const virtualRoot = ReactDOM.createRoot(hiddenContainer);
                    virtualRoot.render(
                        <MindMap
                            mindMapSummaryListData={mindmapTree.children}
                            courseName={mindmapTree.title}
                            width={2400}
                            height={1600}
                            size="16"
                        />
                    );

                    // 🔥 等待虚拟思维导图渲染完成，使用轮询检查确保canvas真正渲染完成
                    let virtualCanvas = null;
                    let attempts = 0;
                    const maxAttempts = 20; // 最多尝试20次，每次等待200ms，总共最多4秒

                    while (attempts < maxAttempts) {
                        await new Promise(resolve => setTimeout(resolve, 200));
                        virtualCanvas = hiddenContainer.querySelector('canvas');

                        // 检查canvas是否存在且已渲染（宽度和高度都大于0）
                        if (virtualCanvas && virtualCanvas.width > 0 && virtualCanvas.height > 0) {
                            // 额外等待一小段时间，确保所有节点都已绘制完成
                            await new Promise(resolve => setTimeout(resolve, 100));
                            break;
                        }
                        attempts++;
                    }

                    // 获取虚拟思维导图的完整 canvas
                    if (virtualCanvas && virtualCanvas.width > 0 && virtualCanvas.height > 0) {
                        // 使用虚拟 canvas 的完整内容（先不添加水印，后面统一添加）
                        const downloadCanvas = document.createElement('canvas');
                        const ctx = downloadCanvas.getContext('2d');
                        const srcW = virtualCanvas.width;
                        const srcH = virtualCanvas.height;
                        downloadCanvas.width = srcW;
                        downloadCanvas.height = srcH;

                        ctx.fillStyle = '#FFFFFF';
                        ctx.fillRect(0, 0, downloadCanvas.width, downloadCanvas.height);
                        ctx.drawImage(virtualCanvas, 0, 0, srcW, srcH, 0, 0, srcW, srcH);

                        // 🔥 先导出为 dataURL，然后会在后面的水印添加步骤中处理
                        imageDataUrl = downloadCanvas.toDataURL('image/png', 1.0);
                    } else {
                        // 如果虚拟 canvas 获取失败，回退到使用当前 canvas
                        const downloadCanvas = document.createElement('canvas');
                        const ctx = downloadCanvas.getContext('2d');
                        const srcW = mindMapCanvas.width;
                        const srcH = mindMapCanvas.height;
                        downloadCanvas.width = srcW;
                        downloadCanvas.height = srcH;
                        ctx.fillStyle = '#FFFFFF';
                        ctx.fillRect(0, 0, downloadCanvas.width, downloadCanvas.height);
                        ctx.drawImage(mindMapCanvas, 0, 0, srcW, srcH, 0, 0, srcW, srcH);
                        imageDataUrl = downloadCanvas.toDataURL('image/png', 1.0);
                    }

                    // 清理虚拟容器
                    virtualRoot.unmount();
                    document.body.removeChild(hiddenContainer);
                }
            } else {
                // 如果没有 canvas，尝试获取图片
                const img = contentRef.current?.querySelector('img');
                if (img && img.src && !img.src.includes('data:image/svg')) {
                    const tempImg = new Image();
                    tempImg.crossOrigin = 'anonymous';
                    tempImg.src = img.src;

                    await new Promise((resolve, reject) => {
                        const timeout = setTimeout(() => reject(new Error('图片加载超时')), 10000);
                        tempImg.onload = () => {
                            clearTimeout(timeout);
                            resolve();
                        };
                        tempImg.onerror = () => {
                            clearTimeout(timeout);
                            reject(new Error('图片加载失败'));
                        };
                    });

                    const tempCanvas = document.createElement('canvas');
                    tempCanvas.width = tempImg.width;
                    tempCanvas.height = tempImg.height;
                    const ctx = tempCanvas.getContext('2d');
                    ctx.drawImage(tempImg, 0, 0);
                    imageDataUrl = tempCanvas.toDataURL('image/png');
                } else if (data.imageUrl) {
                    // 如果有 imageUrl，直接加载
                    const tempImg = new Image();
                    tempImg.crossOrigin = 'anonymous';
                    tempImg.src = data.imageUrl;

                    await new Promise((resolve, reject) => {
                        const timeout = setTimeout(() => reject(new Error('图片加载超时')), 10000);
                        tempImg.onload = () => {
                            clearTimeout(timeout);
                            resolve();
                        };
                        tempImg.onerror = () => {
                            clearTimeout(timeout);
                            reject(new Error('图片加载失败'));
                        };
                    });

                    const tempCanvas = document.createElement('canvas');
                    tempCanvas.width = tempImg.width;
                    tempCanvas.height = tempImg.height;
                    const ctx = tempCanvas.getContext('2d');
                    ctx.drawImage(tempImg, 0, 0);
                    imageDataUrl = tempCanvas.toDataURL('image/png');
                }
            }

            if (!imageDataUrl) {
                ToastUtils.showToast('无法获取思维导图数据，请确保思维导图已加载完成');
                return;
            }

            // 添加"内容由AI生成"水印
            const img = new Image();
            img.src = imageDataUrl;

            await new Promise((resolve, reject) => {
                const timeout = setTimeout(() => reject(new Error('图片处理超时')), 10000);
                img.onload = () => {
                    clearTimeout(timeout);
                    resolve();
                };
                img.onerror = () => {
                    clearTimeout(timeout);
                    reject(new Error('图片处理失败'));
                };
            });

            const watermarkCanvas = document.createElement('canvas');
            const dpr = window.devicePixelRatio || 1;
            // 计算AI标识所需的高度（根据图片尺寸动态计算）
            const baseFontSize = 24;
            const fontSize = Math.max(baseFontSize, Math.floor(baseFontSize * (img.width / 1000)));
            const watermarkText = '内容由AI生成';
            // 测量文本宽度以确定所需空间
            const tempCtx = document.createElement('canvas').getContext('2d');
            tempCtx.font = `${fontSize}px Arial, "Microsoft YaHei", sans-serif`;
            const textMetrics = tempCtx.measureText(watermarkText);
            const watermarkHeight = fontSize + Math.max(24, Math.floor(24 * (img.width / 1000))) * 2; // 字体高度 + 上下padding

            // 增加canvas高度，为AI标识留出空间
            watermarkCanvas.width = img.width;
            watermarkCanvas.height = img.height + watermarkHeight;
            const ctx = watermarkCanvas.getContext('2d');

            // 先填充白色背景
            ctx.fillStyle = '#FFFFFF';
            ctx.fillRect(0, 0, watermarkCanvas.width, watermarkCanvas.height);

            // 绘制原图（在canvas的上半部分）
            ctx.drawImage(img, 0, 0);

            // 🔥 添加"内容由AI生成"水印（在思维导图下方，靠右对齐）
            ctx.fillStyle = 'rgba(153, 153, 153, 0.8)';
            ctx.font = `${fontSize}px Arial, "Microsoft YaHei", sans-serif`;
            ctx.textAlign = 'right';
            ctx.textBaseline = 'bottom';
            // padding 也相应增大，确保水印位置合适
            const padding = Math.max(24, Math.floor(24 * (watermarkCanvas.width / 1000)));
            // 将AI标识绘制在思维导图下方，靠右对齐
            ctx.fillText(watermarkText, watermarkCanvas.width - padding, img.height + watermarkHeight - padding);

            // 转换为 blob 并下载
            watermarkCanvas.toBlob((blob) => {
                if (blob) {
                    const url = URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = '思维导图.png';
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    URL.revokeObjectURL(url);
                }
            }, 'image/png');
        } catch (error) {
            console.error('下载思维导图失败:', error);
        }
    };

    // 根据类型渲染不同的内容组件
    const renderContent = () => {
        switch (contentType) {
            case 'video_player':
                return <VideoPlayerContent data={data} />;
            case 'video_play':
                return (
                    <div style={{ padding: '0 16px 16px' }}>
                        <VideoPlayer
                            videoUrl={data.videoUrl}
                            poster={data.poster}
                            title={data.title}
                            autoPlay={true}
                            controls={true}
                            height="520px"
                        />
                        {data.description && (
                            <div style={{ marginTop: '16px', color: '#666', lineHeight: '22px' }}>
                                {data.description}
                            </div>
                        )}
                    </div>
                );

            case 'mind_map':
                return <MindMapContent data={data} />;

            case 'video_list':
                return (
                    <VideoListContent
                        videos={videoList}
                        onVideoClick={onVideoClick}
                        onDetailClick={onDetailClick}
                        onContinueAsk={onContinueAsk}
                    />
                );

            case 'video_detail':
                return (
                    <VideoDetailContent
                        video={data.video}
                        videoIndex={data.videoIndex}
                        data={data}  // 传递完整的 data 对象，包含 videoIndex
                        videoList={videoList}
                        onViewNext={onViewNext}
                        onContinueAsk={onContinueAsk}
                        onVideoClick={onVideoClick}
                    />
                );

            case 'text_summary':
            default:
                return <TextSummaryContent data={data} />;
        }
    };

    return (
        <>
            {/* 侧边抽屉 */}
            <div
                ref={drawerRef}
                className={`${s['drawer-container']} ${visible ? s['open'] : ''}`}
            >
                {/* 抽屉头部 */}
                <div className={s['drawer-header']}>
                    <h2 className={s['drawer-title']}>{getTitle()}</h2>
                    <div className={s['header-actions']}>
                        {/* 只在文字总结模式下显示复制按钮 */}
                        {showCopyButton && (
                            <Tooltip
                                title={copyState.status === 'success' ? '已复制' : '复制'}
                                placement="top"
                                autoAdjustOverflow={false}
                            >
                                <span
                                    className={`${s['copy-btn']} ${copyState.status === 'success' ? s['copied'] : ''}`}
                                    onClick={handleCopyMarkdown}
                                >
                                    {copyState.status === 'success' ? (
                                        <img
                                            src={checkmarkImg}
                                            alt="已复制"
                                            style={{ width: '30px', height: '30px', display: 'block' }}
                                        />
                                    ) : (
                                        <img
                                            src={copyIconImg}
                                            alt="复制"
                                            style={{ width: '20px', height: '20px', display: 'block' }}
                                        />
                                    )}
                                </span>
                            </Tooltip>
                        )}
                        {/* 只在思维导图模式下显示下载按钮 */}
                        {showDownloadButton && (
                            <span
                                className={s['download-btn']}
                                onClick={handleDownloadMindMap}
                                title="下载思维导图"
                            >
                                <i className="iconfont icon-xiazai" style={{ fontSize: '18px' }}></i>
                                下载图片
                            </span>
                        )}
                        {/* 只在视频详情模式下显示返回按钮 */}
                        {showBackButton && (
                            <span
                                className={s['back-btn']}
                                onClick={onBackToList}
                                title="返回视频列表"
                            >
                                <i className="iconfont icon-huidao"></i>
                                <span>返回视频列表</span>
                            </span>
                        )}
                        <Tooltip title="关闭" placement="top" autoAdjustOverflow={false}>
                            <span className={s['close-btn']} onClick={onClose}>
                                <Button
                                    type="text"
                                    icon={<CloseOutlined />}
                                    style={{ color: '#666666' }}
                                />
                            </span>
                        </Tooltip>
                    </div>
                </div>

                {/* 抽屉内容区域 */}
                <div className={`${s['drawer-content']} ${contentType === 'mind_map' ? s['drawer-content--mindmap'] : ''}`} ref={contentRef}>
                    {renderContent()}
                </div>
            </div>
        </>
    );
};

export default DetailModal;
